import { EditorView } from '@codemirror/view'
import { Extension } from '@codemirror/state'
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language'
import { tags as t } from '@lezer/highlight'


// Colors from https://www.nordtheme.com/docs/colors-and-palettes
// Polar Night
const base00 = '#2e3440', // black
  base01 = '#3b4252', // dark grey
  base02 = '#434c5e',
  base03 = '#4c566a' // grey

// Snow Storm
const //base04 = '#d8dee9', // grey
  base05 = '#e5e9f0', // off white
  base06 = '#eceff4' // white

// Frost
const base07 = '#8fbcbb', // moss green
  base08 = '#88c0d0', // ice blue
  base09 = '#81a1c1', // water blue
  base0A = '#5e81ac' // deep blue

// Aurora
const base0b = '#bf616a', // red
  base0C = '#d08770', // orange
  base0D = '#ebcb8b', // yellow
  base0E = '#a3be8c', // green
  base0F = '#b48ead' // purple

const invalid = '#d30102',
  darkBackground = base06,
  highlightBackground = darkBackground,
  background = 'transparent',
  keyword = '#aa4d00',
  tooltipBackground = base05,
  selection = darkBackground,
  cursor = base01


// tunepad light
const tbase = '#111b27';
const tkeyword = 'rgb(133, 57, 216)';
const tvariable = tbase;
const tnumber = '#7d6600';
const tboolean = '#7d6600';
const tstring = '#237800';
const tfuncname = '#8600c6';
const tclassname = '#005c99';
const toperator = tbase; //'rgb(133, 57, 216)'; //'#aa4d00';
const tpunctuation = tbase;
const tcomment = 'rgb(130, 134, 139)';

/// The editor theme styles for Basic Light.
export const basicLightTheme = EditorView.theme(
  {
    '&': {
      color: base00,
      backgroundColor: background
    },

    '.cm-content': {
      caretColor: cursor
    },

    '.cm-cursor, .cm-dropCursor': { 
        borderLeftColor: cursor,
        borderLeftWidth: '2px'
    },
    '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection':
      { backgroundColor: selection },

    '.cm-panels': { backgroundColor: darkBackground, color: base03 },
    '.cm-panels.cm-panels-top': { borderBottom: '2px solid black' },
    '.cm-panels.cm-panels-bottom': { borderTop: '2px solid black' },

    '.cm-searchMatch': {
      backgroundColor: '#72a1ff59',
      outline: `1px solid ${base03}`
    },
    '.cm-searchMatch.cm-searchMatch-selected': {
      backgroundColor: base05
    },

    '.cm-activeLine': { 
        outline: 'none', 
        backgroundColor: '#fff'
    },
    '.cm-selectionMatch': { backgroundColor: 'pink' },

    '&.cm-focused .cm-matchingBracket, &.cm-focused .cm-nonmatchingBracket': {
      //outline: `1px solid ${base03}`
      backgroundColor: '#fff6'
    },

    '&.cm-focused .cm-matchingBracket': {
      backgroundColor: base06
    },

    '.cm-gutters': {
      backgroundColor: 'rgb(237, 241, 251)',
      paddingLeft: '5px',
      color: '#898989',
      border: 'none',
      borderRight: '1px solid #aaa'
    },

    '.cm-activeLineGutter': {
      backgroundColor: 'transparent' //'#0001'
    },

    '.cm-foldPlaceholder': {
      backgroundColor: 'transparent',
      border: 'none',
      color: '#ddd'
    },

    '.cm-tooltip': {
      border: 'none',
      backgroundColor: tooltipBackground
    },
    '.cm-tooltip .cm-tooltip-arrow:before': {
      borderTopColor: 'transparent',
      borderBottomColor: 'transparent'
    },
    '.cm-tooltip .cm-tooltip-arrow:after': {
      borderTopColor: tooltipBackground,
      borderBottomColor: tooltipBackground
    },
    '.cm-tooltip-autocomplete': {
      '& > ul > li[aria-selected]': {
        backgroundColor: highlightBackground,
        color: base03
      }
    }
  },
  { dark: false }
)


/// The highlighting style for code in the Basic Light theme.
export const tunePadLightHighlightStyle = HighlightStyle.define([
  { tag: t.keyword, color: tkeyword },
  {
    tag: [t.name, t.deleted, t.character, t.propertyName, t.macroName],
    color: tbase
  },
  { tag: [t.variableName], color: tvariable },
  { tag: [t.function(t.variableName)], color: tvariable },
  { tag: [t.labelName], color: tvariable },
  {
    tag: [t.color, t.constant(t.name), t.standard(t.name)],
    color: tnumber
  },
  { tag: [t.definition(t.name), t.separator], color: tfuncname },
  { tag: [t.brace], color: tpunctuation },
  {
    tag: [t.annotation],
    color: invalid
  },
  {
    tag: [t.number, t.changed, t.annotation, t.modifier, t.self, t.namespace],
    color: tnumber
  },
  {
    tag: [t.typeName, t.className],
    color: tclassname
  },
  {
    tag: [t.operator, t.operatorKeyword],
    color: toperator
  },
  {
    tag: [t.tagName],
    color: base0F
  },
  {
    tag: [t.squareBracket],
    color: tpunctuation
  },
  {
    tag: [t.angleBracket],
    color: tpunctuation
  },
  {
    tag: [t.attributeName],
    color: base0D
  },
  {
    tag: [t.regexp],
    color: tstring
  },
  {
    tag: [t.quote],
    color: tstring
  },
  { tag: [t.string], color: tstring },
  {
    tag: t.link,
    color: base07,
    textDecoration: 'underline',
    textUnderlinePosition: 'under'
  },
  {
    tag: [t.url, t.escape, t.special(t.string)],
    color: tstring
  },
  { tag: [t.meta], color: base08 },
  { tag: [t.comment], color: tcomment, fontStyle: 'italic' },
  { tag: t.strong, fontWeight: 'bold', color: tbase },
  { tag: t.emphasis, fontStyle: 'italic', color: tbase },
  { tag: t.strikethrough, textDecoration: 'line-through' },
  { tag: t.heading, fontWeight: 'bold', color: tbase },
  { tag: t.special(t.heading1), fontWeight: 'bold', color: tbase },
  { tag: t.heading1, fontWeight: 'bold', color: tbase },
  {
    tag: [t.heading2, t.heading3, t.heading4],
    fontWeight: 'bold',
    color: tbase
  },
  {
    tag: [t.heading5, t.heading6],
    color: tbase
  },
  { tag: [t.atom, t.bool, t.special(t.variableName)], color: tboolean },
  {
    tag: [t.processingInstruction, t.inserted],
    color: base07
  },
  {
    tag: [t.contentSeparator],
    color: tbase
  },
  { tag: t.invalid, color: base02, borderBottom: `1px dotted ${invalid}` }
])

/// Extension to enable the Basic Light theme (both the editor theme and
/// the highlight style).
export const tunePadLight: Extension = [
  basicLightTheme,
  syntaxHighlighting(tunePadLightHighlightStyle)
]
